import {jws} from "jsrsasign";

export type MapkitTokenFetcher = (addOrigin: boolean) => Promise<string>;

export interface SignMapkitTokenParams {
  privateKey: string;
  teamId: string;
  keyId: string;
  origin: string;
  addOrigin: boolean;
}

// Webpack can compile this file twice (TokenService.ts vs the tsc-emitted
// TokenService.js) when some imports use the `.js` specifier and others omit
// it. Module-local state would then not be shared, so the fetcher lives on
// globalThis as well.
const MAPKIT_TOKEN_FETCHER_KEY = "__wsMapkitTokenFetcher";
let tokenFetcher: MapkitTokenFetcher | null = null;

function readTokenFetcher(): MapkitTokenFetcher | null {
  if (typeof globalThis !== "undefined") {
    const shared = (globalThis as Record<string, unknown>)[MAPKIT_TOKEN_FETCHER_KEY];
    if (typeof shared === "function") {
      return shared as MapkitTokenFetcher;
    }
  }
  return tokenFetcher;
}

function writeTokenFetcher(fetcher: MapkitTokenFetcher): void {
  tokenFetcher = fetcher;
  if (typeof globalThis !== "undefined") {
    (globalThis as Record<string, unknown>)[MAPKIT_TOKEN_FETCHER_KEY] = fetcher;
  }
}

function signMapkitToken({privateKey, teamId, keyId, origin, addOrigin}: SignMapkitTokenParams): string {
  const payload: {iss: string; iat: number; exp: number; origin?: string} = {
    iss: teamId,
    iat: Date.now() / 1000,
    exp: Date.now() / 1000 + 2628288
  };

  if (addOrigin) {
    payload.origin = origin;
  }

  const header = {
    kid: keyId,
    typ: "JWT",
    alg: "ES256"
  };

  return jws.JWS.sign(header.alg, header, payload, privateKey);
}

export default class TokenService {
  static configureMapkitTokenFetcher(fetcher: MapkitTokenFetcher): void {
    writeTokenFetcher(fetcher);
  }

  static generateMapkitJsToken(addOrigin: boolean = true, originOverride: string | null = null): string {
    const privateKey = process.env.APPLE_PRIVATE_KEY;
    const teamId = process.env.APPLE_TEAM_ID;
    const keyId = process.env.APPLE_KEY_ID;

    if (!privateKey || !teamId || !keyId) {
      throw new Error("MapKit credentials not configured");
    }

    const origin = originOverride || process.env.APP_URL || "https://go.waterspeedapp.com";
    return signMapkitToken({privateKey, teamId, keyId, origin, addOrigin});
  }

  static async fetchMapkitJsToken(addOrigin: boolean = true): Promise<string> {
    if (typeof window !== "undefined") {
      const fetcher = readTokenFetcher();
      if (!fetcher) {
        throw new Error("MapKit token fetcher not configured");
      }
      return await fetcher(addOrigin);
    }

    return TokenService.generateMapkitJsToken(addOrigin);
  }
}
